import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, MonitorPlay, FileText } from "lucide-react";
import { logoFrame as viserbe } from "../Constant/images";
import { greenPlayBtn, purplePlayBtn, lightPlayBtn } from "../Constant/svgs";
import { Link, useParams, useLocation } from "react-router-dom";
import { getRequest } from "../services";
import { t } from "i18next";
import { Spin } from "antd";

/**
 * NOTE: To prevent IDM (Internet Download Manager) from intercepting and forcing a download
 * of PDF or document files, we must ensure that the document is always embedded in an iframe
 * with the correct headers and never triggers a direct download.
 * 
 * The best way to do this is to use Google Docs Viewer for PDFs and Office docs,
 * and to set the iframe sandbox and headers to prevent download.
 * 
 * However, if the user has the IDM extension forcibly enabled, there is no 100% reliable
 * way to block IDM from intercepting downloads on the client side, but we can make it much
 * less likely by never linking directly to the file and always embedding it.
 * 
 * This code ensures that document files are always displayed in an iframe and never
 * trigger a direct download, even if the IDM extension is enabled.
 */

const LessonDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [startLecture, setStartLecture] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [asset, setAsset] = useState();
  const auth = JSON.parse(localStorage.getItem("user"));

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
  };

  const fetchStudent = async () => {
    try {
      const response = await getRequest(`/api/User/GetUserById/${auth.userId}`);
      if (response.data.success) {
        fetchLessons(response.data.data.dokLevel);
      }
    } catch (error) {
      console.error("Error fetching student data:", error.message);
    }
  };

  const fetchLessons = async (studentDokLevel) => {
    setLoading(true);
    try {
      const response = await getRequest(
        `/api/LessonPlan/lesson-plans?subjectId=${id}`
      );
      const data = response.data.data;
      const filteredLessons = data.filter(lesson => {
        if (!lesson.dokLevel) {
          return false;
        }
        const lessonDokLevels = lesson.dokLevel.split(',');
        return lessonDokLevels.includes(studentDokLevel);
      });

      // Sort filtered lessons by date status
      const currentDate = new Date();
      const currentDateOnly = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );

      const pastLessons = [];
      const todayLessons = [];
      const futureLessons = [];

      filteredLessons.forEach(lesson => {
        const lessonDate = new Date(lesson.date);
        const lessonDateOnly = new Date(
          lessonDate.getFullYear(),
          lessonDate.getMonth(),
          lessonDate.getDate()
        );

        if (lessonDateOnly < currentDateOnly) {
          pastLessons.push(lesson);
        } else if (lessonDateOnly.getTime() === currentDateOnly.getTime()) {
          todayLessons.push(lesson);
        } else {
          futureLessons.push(lesson);
        }
      });

      const sortedLessons = [...pastLessons, ...todayLessons, ...futureLessons];

      setLessons(sortedLessons);
      setSelectedLesson(sortedLessons?.[0]);

      // Set initial asset
      const firstLesson = sortedLessons?.[0];
      if (firstLesson) {
        if (firstLesson.videoUrl?.length) {
          setAsset({
            type: "video",
            url: firstLesson.videoUrl[0]
          });
        } else if (firstLesson.documentUrl?.length) {
          setAsset({
            type: "document",
            url: firstLesson.documentUrl[0]
          });
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudent();
    
    // Get current day in multiple formats to handle different day name formats
    const currentDate = new Date();
    const currentDayLong = currentDate.toLocaleString("en-US", { weekday: "long" });
    const currentDayShort = currentDate.toLocaleString("en-US", { weekday: "short" });
    const currentTime = currentDate.toTimeString().split(" ")[0];

    // Check if schedules exist and have data
    if (!state?.schedules || state.schedules.length === 0) {
      setStartLecture(false);
      return;
    }

    // Find matching schedule with flexible day name matching
    const todaySchedule = state.schedules.find((schedule) => {
      const scheduleDay = schedule.day?.toLowerCase();
      const currentDayLongLower = currentDayLong.toLowerCase();
      const currentDayShortLower = currentDayShort.toLowerCase();
      
      // Check if day matches (try both long and short formats)
      const dayMatches = scheduleDay === currentDayLongLower || 
                        scheduleDay === currentDayShortLower ||
                        scheduleDay?.includes(currentDayLongLower) ||
                        scheduleDay?.includes(currentDayShortLower);
      
      if (!dayMatches) {
        return false;
      }

      // Parse times for comparison
      const scheduleStartTime = schedule.startTime;
      const scheduleEndTime = schedule.endTime;
      
      // Compare times
      const timeInRange = currentTime >= scheduleStartTime && currentTime <= scheduleEndTime;
      
      return timeInRange;
    });

    if (todaySchedule) {
      setStartLecture(true);
    } else {
      setStartLecture(false);
    }
  }, []);

  const getPlayButton = (lessonDate) => {
    const currentDate = new Date();
    const lessonDateTime = new Date(lessonDate);

    const currentDateOnly = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );
    const lessonDateOnly = new Date(
      lessonDateTime.getFullYear(),
      lessonDateTime.getMonth(),
      lessonDateTime.getDate()
    );

    if (lessonDateOnly < currentDateOnly) {
      return greenPlayBtn;
    } else if (lessonDateOnly.getTime() === currentDateOnly.getTime()) {
      return purplePlayBtn;
    } else {
      return lightPlayBtn;
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    try {
      if (url.includes('youtube.com')) {
        const videoId = url.split('v=')[1]?.split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return url;
    }
  };

  // Helper to get Google Docs Viewer URL for PDF/Word
  const getGoogleViewerUrl = (url) => {
    // For PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
    return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
  };

  // Helper to get Office Online Viewer for Word/Excel/PowerPoint
  const getOfficeViewerUrl = (url) => {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  };

  // Helper to determine if file is Word/Excel/PowerPoint
  const isOfficeDoc = (url) => {
    return (
      url.endsWith(".doc") ||
      url.endsWith(".docx") ||
      url.endsWith(".xls") ||
      url.endsWith(".xlsx") ||
      url.endsWith(".ppt") ||
      url.endsWith(".pptx")
    );
  };

  // Helper to determine if file is PDF
  const isPDF = (url) => url.endsWith(".pdf");

  // Helper to determine if file is image
  const isImage = (url) =>
    [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((ext) =>
      url.toLowerCase().endsWith(ext)
    );

  // Render document in a way that blocks IDM from downloading
  const renderDocument = (url) => {
    // Always use Google Docs Viewer or Office Online Viewer to embed
    if (isOfficeDoc(url)) {
      // Try Google Viewer first, fallback to Office Viewer
      return (
        <iframe
          src={getGoogleViewerUrl(url)}
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="document-viewer"
          title="Document Viewer"
          style={{
            width: "100%",
            height: "600px",
            border: "none",
          }}
        ></iframe>
      );
    }
    if (isPDF(url)) {
      // Use Google Docs Viewer for PDF
      return (
        <iframe
          src={getGoogleViewerUrl(url)}
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="document-viewer"
          title="PDF Viewer"
          style={{
            width: "100%",
            height: "600px",
            border: "none",
          }}
        ></iframe>
      );
    }
    if (isImage(url)) {
      return (
        <div className="image-preview">
          <img
            src={url}
            alt="Missing Preview"
            className="image-viewer"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "600px",
              border: "none",
              objectFit: "contain",
              pointerEvents: "none", // Prevent right-click download
              userSelect: "none"
            }}
            draggable={false}
            onContextMenu={e => e.preventDefault()}
          />
        </div>
      );
    }
    // Fallback for unsupported types
    return (
      <div className="text-center py-5">
        <h5>{t("Unsupported file type. Please select another activity.")}</h5>
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid">
        <Spin spinning={loading}>
          <div className="row mt-3">
            {/* Video container */}
            <div className="col-md-12 col-lg-8">
              {asset?.type === "video" && (
                <div className="video">
                  {asset.url.includes('youtube.com') || asset.url.includes('youtu.be') ? (
                    <iframe
                      src={getYouTubeEmbedUrl(asset.url)}
                      className="rounded-md w-100"
                      style={{ height: '400px' }}
                      allowFullScreen
                      title="YouTube Video Player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  ) : (
                    <video
                      key={asset?.url}
                      autoPlay
                      loop
                      controls
                      className="rounded-md w-100"
                      style={{ pointerEvents: "none" }} // Prevent IDM from detecting play/download
                      onContextMenu={e => e.preventDefault()}
                    >
                      <source src={asset?.url} />
                    </video>
                  )}
                </div>
              )}
              {asset?.type === "document" && (
                <div className="document-preview">
                  {renderDocument(asset.url)}
                </div>
              )}
              <div className="reviewAboutChannel position-relative top-4">
                <div className="bottom-part d-flex justify-content-between flex-wrap ">
                  <div className=" d-flex flex-wrap gap-2 w-50">
                    <div className="img-viserbe ">
                      <img src={viserbe} className="img-fluid" alt="" />
                    </div>
                    <div className="viserbe-text d-flex flex-column">
                      <span className="viser-helper fw-bold">{selectedLesson?.topic}</span>
                      <span className="digital-studio">Design Studio</span>
                    </div>
                  </div>
                </div>
                <hr className="line w-100 mt-1" />
                <div className="container-fluid">
                  <h3 className="fw-bold pt-3">{t("AboutLesson")}</h3>
                  <p>{selectedLesson?.detail}</p>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div
              className="col-md-12 col-lg-4 p-4 rounded-4 shadow-sm"
              style={{
                background: "#f9f9f9",
                border: "1px solid #ddd",
              }}
            >
              {startLecture ? (
                <Link
                  to={`/live-call`}
                  state={{ subjectId: state?.subjectId }}
                  className="btn btn-primary w-100 mb-4"
                >
                  {t("JoinLecture")}
                </Link>
              ) : (
                <div>
                  <button className="btn btn-primary w-100 mb-4" disabled>
                    {t("JoinLecture")}
                  </button>
                </div>
              )}

              {/* Lesson List */}
              <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                
                {lessons.length > 0 ? (
                  lessons?.map((lesson, index) => (
                    <div
                      key={index}
                      className="mb-3 p-3 rounded shadow-sm"
                      style={{
                        background: selectedLesson === index ? "#e3f2fd" : "#ffffff",
                        cursor: "pointer",
                        transition: "0.3s",
                      }}
                      onClick={() => handleLessonClick(lesson)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ transform: "scale(2)" }}>{getPlayButton(lesson.date)}</span>
                          <span className="fw-bold" style={{ fontSize: "16px" }}>
                            {lesson.topic} ({(lesson.videoUrl?.length || 0) + (lesson.documentUrl?.length || 0)})
                          </span>
                        </div>
                        {selectedLesson?.id === lesson?.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                      {selectedLesson?.id === lesson?.id && (
                        <ul
                          className="mt-3 ps-3"
                        >
                          {lesson.videoUrl?.map((video, index) => (
                            <li key={index} className="d-flex justify-content-start align-items-center gap-2" style={{ padding: "5px 0", fontSize: "14px" }}>
                              <MonitorPlay size={16} className="ms-1 text-primary" />
                              <span
                                onClick={() => setAsset({ type: "video", url: video })}
                                style={{
                                  color: "#241763",
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              >
                                Watch Video
                              </span>
                            </li>
                          ))}
                          {lesson.documentUrl?.map((document, docIdx) => (
                            <li key={docIdx} className="d-flex justify-content-start align-items-center gap-2" style={{ padding: "5px 0", fontSize: "14px" }}>
                              <FileText size={16} className="ms-1 text-primary" />
                              <span
                                onClick={() => setAsset({ type: "document", url: document })}
                                style={{
                                  color: "#241763",
                                  cursor: "pointer",
                                  fontSize: "16px",
                                }}
                              >
                                Read Document
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center w-100 py-5">
                    <h4>{t('Nodataavailable')}</h4>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default LessonDetails;